"use client"

import { useMemo, useSyncExternalStore } from "react"
import { ScrollCarousel } from "./scroll-carousel"
import { ProductCarouselCard, type CarouselProduct } from "./product-carousel-card"
import { RECENT_EVENT } from '@/lib/recently-viewed-store'

export interface RecentlyViewedItem {
  /** Stable id used to match against localStorage entries */
  id: string | number
  /** Pre-mapped CarouselProduct ready for `ProductCarouselCard` */
  product: CarouselProduct
}

interface RecentlyViewedCarouselProps {
  /**
   * Pool of products to filter against the visitor's localStorage history.
   * Render the full pool client-side; the component filters down to only
   * the IDs the visitor has actually viewed, in their visit order.
   */
  pool: RecentlyViewedItem[]
  /** localStorage key. Default: `"fridge_recently_viewed"`. */
  storageKey?: string
  /** Section eyebrow text. Default: `"Recently viewed"`. */
  eyebrow?: string
  /** Section headline. Default: `"Pick up where you left off"`. */
  title?: string
  /** Background class for the wrapping section. Default: `"bg-gray-50"`. */
  bgClass?: string
}

/**
 * Reads `localStorage[storageKey]` (an array of product IDs in visit order)
 * and returns the parsed list. Returns `null` while the read is pending so
 * SSR vs CSR don't disagree on initial render.
 */
function useRecentlyViewedIds(storageKey: string): string[] | null {
  const raw = useSyncExternalStore(
    (notify) => {
      window.addEventListener('storage', notify)
      window.addEventListener(RECENT_EVENT, notify)
      return () => {
        window.removeEventListener('storage', notify)
        window.removeEventListener(RECENT_EVENT, notify)
      }
    },
    () => { try { return window.localStorage.getItem(storageKey) || '[]' } catch { return '[]' } },
    () => null,
  )
  return useMemo(() => {
    if (raw === null) return null
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : []
    } catch { return [] }
  }, [raw])
}

/**
 * RecentlyViewedCarousel — conditional carousel that only renders when the
 * visitor has products in `localStorage[storageKey]`. Filters the server-
 * provided `pool` down to the IDs they actually viewed, in their visit
 * order, then renders each as a `ProductCarouselCard` inside a
 * `ScrollCarousel`.
 *
 * Returns `null` for fresh visitors and during the localStorage read window
 * so we never paint a section that may immediately disappear.
 *
 * Lifted from the editorial mockups (blog-post + blogs-news), where the same
 * client-only conditional rail was inlined twice. Now graduated to brand-kit
 * for reuse on any editorial surface that wants a "pick up where you left
 * off" rail without flashing on visitors who don't qualify.
 */
export function RecentlyViewedCarousel({
  pool,
  storageKey = "fridge_recently_viewed",
  eyebrow = "Recently viewed",
  title = "Pick up where you left off",
  bgClass = "bg-gray-50",
}: RecentlyViewedCarouselProps) {
  const ids = useRecentlyViewedIds(storageKey)
  if (ids === null || ids.length === 0) return null

  const byId = new Map(pool.map((item) => [String(item.id), item]))
  const ordered = ids
    .map((id) => byId.get(id))
    .filter((item): item is RecentlyViewedItem => !!item)

  if (ordered.length === 0) return null

  return (
    <ScrollCarousel eyebrow={eyebrow} title={title} bgClass={bgClass}>
      {ordered.map((item) => (
        <ProductCarouselCard key={item.id} product={item.product} />
      ))}
    </ScrollCarousel>
  )
}
